import React, { useCallback, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import { SideLoadedBusinessColumnDefs } from "constants/columnDefinitions/sideLoadedBusiness";

import { getSideLoadedBusinesses, searchSideLoadedBusinesses } from "selectors";
import { useDebounce } from "@react-hook/debounce";
import SearchInput from "components/SearchInput";

import styled from "styled-components/macro";

import {
  getSideLoadedBusinessRowId,
  useSideLoadedBusinessGridContext,
} from "./useSideLoadedBusinessGridContext";

interface SideLoadedBusinessTableProps {
  initialSearchTerm?: string;
  onClose: () => void;
  onSelect: (recordId: string) => void;
}

export const SideLoadedBusinessTableWrapper = styled(TableWrapper)(
  ({ minHeight = 400 }) => `
  min-height: ${minHeight}px;
`,
);
const SideLoadedBusinessTable = ({
  initialSearchTerm = "",
  onClose,
  onSelect,
}: SideLoadedBusinessTableProps): React.JSX.Element => {
  // Use our Fuse search results, rather than the embedded AgGrid one in order to give
  // consistent results.
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    300,
  );
  React.useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [setSearchTerm, debouncedSearchTerm]);
  React.useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [setDebouncedSearchTerm, searchTerm]);

  const sideLoadedBusinesses = useSelector(getSideLoadedBusinesses);

  const sideLoadStrictResults = useSelector(
    searchSideLoadedBusinesses(debouncedSearchTerm, true),
  );
  const sideLoadLiberalResults = useSelector(
    searchSideLoadedBusinesses(debouncedSearchTerm, false),
  );
  const sideLoadResults = sideLoadStrictResults.length
    ? sideLoadStrictResults
    : sideLoadLiberalResults;

  const sideLoadIds = useMemo(
    () =>
      debouncedSearchTerm
        ? sideLoadResults.map(result => result.item.id)
        : null,
    [sideLoadResults, debouncedSearchTerm],
  );

  // Show all results when there is no search term.
  const rowData = useMemo(() => {
    if (!sideLoadIds) {
      return Object.values(sideLoadedBusinesses);
    }
    // keep ordering of Fuse results for initial order in Ag-Grid
    return sideLoadIds.map(id => sideLoadedBusinesses[id]).filter(Boolean);
  }, [sideLoadedBusinesses, sideLoadIds]);

  const handleSelectAndCompare = useCallback(
    (selectedRecordId: string) => {
      if (selectedRecordId) {
        onSelect(selectedRecordId);
      }
    },
    [onSelect],
  );

  const context = useSideLoadedBusinessGridContext(handleSelectAndCompare);

  const standardPreamble =
    "Not finding what you're looking for? You can edit your search or fix spelling to see more";
  const noSearchTermsPreamble = "Enter search text to search suggestions";
  const noResultsPreamble =
    "No suggestions found. You can edit the search to fix spelling or include fewer words.";
  const noExactResultsPreamble =
    "No exact matches found. Searching for any of the entered terms instead. You can try fixing spelling, removing generic terms or including fewer terms to see exact matches.";

  let preamble = standardPreamble;
  if (debouncedSearchTerm.length === 0) {
    preamble = noSearchTermsPreamble;
  } else if (rowData.length === 0) {
    preamble = noResultsPreamble;
  } else if (!sideLoadStrictResults.length) {
    preamble = noExactResultsPreamble;
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose}>Data Suggestions</DialogTitle>
      <DialogContent dividers>
        <Column full>
          <Row paddingVertical={1}>
            <SmallText>
              {preamble} Suggestions are sourced from your supplied Legacy
              Business data.
            </SmallText>
          </Row>
          <Row fullWidth>
            <SearchInput
              value={searchTerm}
              handleChange={setSearchTerm}
              style={{ marginLeft: 0 }}
            />
          </Row>
          <AgGridTable
            columnDefs={SideLoadedBusinessColumnDefs}
            rowData={rowData}
            getRowId={getSideLoadedBusinessRowId}
            hasExportCsvPermissions={false}
            tableName={AgGridTables.SIDELOADED_BUSINESSES}
            WrapperComponent={SideLoadedBusinessTableWrapper}
            showGlobalSearchFilter={false}
            hideSavedViews
            context={context}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
};

export default SideLoadedBusinessTable;
